import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

type Tone = 'danger' | 'neutral';

interface ConfirmOptions {
  title: string;
  description?: string;
  // Optional supplemental notes rendered as tinted callouts (e.g. red warnings,
  // amber disclosures). Same palette as site-wide banners.
  warning?: string;
  info?: string;
  confirmText?: string;
  cancelText?: string;
  tone?: Tone; // 'danger' uses red confirm, 'neutral' uses forest. Default: 'neutral'.
}

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setPending({ ...opts, resolve });
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setPending(null);
  }, []);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {pending && <ConfirmDialog options={pending} onSettle={settle} />}
    </ConfirmContext.Provider>
  );
};

const ConfirmDialog: React.FC<{ options: ConfirmOptions; onSettle: (v: boolean) => void }> = ({ options, onSettle }) => {
  const tone: Tone = options.tone ?? 'neutral';
  const confirmBtnClass =
    tone === 'danger'
      ? 'bg-red-600 hover:bg-red-700 text-white'
      : 'bg-forest-800 hover:bg-forest-700 text-cream-100';
  const iconWrapClass = tone === 'danger' ? 'bg-red-100' : 'bg-forest-50';
  const iconColorClass = tone === 'danger' ? 'text-red-600' : 'text-forest-600';

  return (
    <div
      className="fixed inset-0 bg-forest-900/40 backdrop-blur-sm flex items-center justify-center z-[90] p-4 animate-fade-in"
      onClick={() => onSettle(false)}
    >
      <div
        className="bg-cream-50 rounded-2xl shadow-elevated max-w-md w-full p-6 sm:p-8 animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-center w-14 h-14 mx-auto mb-5 rounded-xl ${iconWrapClass}`}>
          <svg className={`w-7 h-7 ${iconColorClass}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d={tone === 'danger'
              ? 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z'
              : 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z'} />
          </svg>
        </div>

        <h3 className="text-xl font-bold text-forest-900 text-center mb-2">{options.title}</h3>
        {options.description && (
          <p className="text-sm text-warm-600 text-center mb-4 whitespace-pre-line">{options.description}</p>
        )}

        {(options.warning || options.info) && (
          <div className="space-y-2.5 mb-6">
            {options.warning && (
              <div className="flex items-start gap-2.5 p-3 bg-red-50 rounded-xl border border-red-200">
                <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <p className="text-xs text-red-700 font-medium leading-relaxed whitespace-pre-line">{options.warning}</p>
              </div>
            )}
            {options.info && (
              <div className="flex items-start gap-2.5 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
                <p className="text-xs text-amber-700 leading-relaxed whitespace-pre-line">{options.info}</p>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => onSettle(false)}
            className="flex-1 px-4 py-3 border border-warm-200 text-forest-700 rounded-lg hover:bg-cream-100 transition-all duration-200 font-medium text-sm cursor-pointer"
          >
            {options.cancelText ?? 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => onSettle(true)}
            className={`flex-1 px-4 py-3 rounded-lg transition-all duration-200 font-medium text-sm cursor-pointer ${confirmBtnClass}`}
          >
            {options.confirmText ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx.confirm;
};
